import { CHECKOUT_ITEM_TYPE_FLIGHT } from 'constants/checkout'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { FlightsClassTypes } from 'constants/flight'
import { FlightSegment, FlightViewTypes } from 'constants/flight'
import { last } from 'lib/array/arrayUtils'
import { getEffectiveFlightPrice, isCarryOnBaggageIncluded, isCheckedBaggageIncluded, isFnfPriceActive } from 'lib/flights/flightUtils'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { CHARTER } from 'constants/provider'
import moment from 'moment'

/**
 * Returns the appropriate expiry timestamp for a flight.
 * Charter flights use departure minus 2 days as expiry (minimum lead time for booking),
 * which is far enough in the future that no countdown timer is shown.
 * Non-charter flights use the provided search-based expiry.
 */
export function getFlightExpiry(journey: App.JourneyV2, searchExpiry: number): number {
  if (journey.provider === CHARTER) {
    return moment(journey.flightGroup.flights[0].departureDateTime).subtract(2, 'days').toDate().getTime()
  }
  return searchExpiry
}

export function getJourneyFields(
  journey: App.JourneyV2,
  requiredSeats?: number,
) {
  const effectivePrice = getEffectiveFlightPrice(journey, requiredSeats)
  return {
    journeyId: journey.id,
    journeyKey: journey.bookingInfo.journeyKey,
    provider: journey.provider,
    providerSearchId: journey.bookingInfo.providerSearchId,
    cost: effectivePrice.all.totalFare,
    carrierImage: {
      url: journey.carrierLogo,
      title: journey.carrierName,
    },
    hasIncludedCheckedBaggage: isCheckedBaggageIncluded(journey),
    hasIncludedCarryOnBaggage: isCarryOnBaggageIncluded(journey),
    paymentScheduleSupported: journey.paymentScheduleSupported,
  }
}

function getViewType(
  departing?: App.AnyJourney,
  returning?: App.AnyJourney,
  viewType?: string,
) {
  const { RETURN, TWO_ONE_WAYS_AND_RETURN } = FlightViewTypes
  const isReturnCombinedFlight = departing?.fareType === RETURN && returning?.fareType === RETURN
  return isReturnCombinedFlight ? TWO_ONE_WAYS_AND_RETURN : viewType
}

interface FlightItemUpdate {
  originAirportCode?: string
  destinationAirportCode?: string
  searchId?: string
  forceBundleId?: string
}

export const EmptyBaggage: App.Checkout.FlightItemPaxBaggageMap = { baggage: {}, carryOnBaggage: {} }

export function setFlightCheckoutItemJourney(
  checkoutItem: App.Checkout.FlightItem,
  journey: App.JourneyV2,
  segment: 'departing' | 'returning',
  expiry: number,
  options: {
    fareFamily?: App.FlightFareFamily
    baggage?: App.Checkout.FlightItemPaxBaggageMap
    forceBundleId?: string
    selectedCabinClass?: FlightsClassTypes
    areAllFlightsCovered?: boolean
  },
) {
  const requiredSeats = checkoutItem.passengers.filter(passenger => passenger.type !== 'infant').length
  const journeyFields = getJourneyFields(journey, requiredSeats)
  const effectivePrice = getEffectiveFlightPrice(journey, requiredSeats)
  const isFnfApplied = isFnfPriceActive(journey, requiredSeats)
  const syncedFareFamily = isFnfApplied && options.fareFamily ? {
    ...options.fareFamily,
    cost: {
      ...options.fareFamily.cost,
      adult: effectivePrice.adult.totalFare ?? 0,
      children: effectivePrice.child?.totalFare ?? 0,
      infant: effectivePrice.infant?.totalFare ?? 0,
      all: effectivePrice.all.totalFare,
    },
  } : options.fareFamily

  const flights = [FlightSegment.DEPARTING, FlightSegment.RETURNING]
    .map((currentSegment): App.Checkout.FlightItemFlight | undefined => {
      const flight = currentSegment === FlightSegment.DEPARTING ? checkoutItem.flights[0] : checkoutItem.flights[1]

      if (segment === currentSegment) {
        const firstFlight = journey.flightGroup.flights[0]
        const lastFlight = last(journey.flightGroup.flights)

        return {
          ...journeyFields,
          carrier: firstFlight?.marketingAirline.code,
          extras: options.baggage ?? EmptyBaggage,
          fareFamily: syncedFareFamily,
          departingAirportCode: firstFlight?.departureAirport,
          arrivalAirportCode: lastFlight?.arrivalAirport,
          departingDate: firstFlight?.departingDate,
          arrivalAirportName: lastFlight?.arrivalAirportName,
          departingAirportName: firstFlight?.departureAirportName,
          arrivalDateTime: lastFlight?.arrivalDateTime,
          departureDateTime: firstFlight?.departureDateTime,
          duration: journey.flightGroup.totalFlightDuration,
          numberOfStops: journey.flightGroup.flights.length - 1,
          layoverDuration: journey.flightGroup.totalStopOverTime,
          ...(options.areAllFlightsCovered ? { withEmbeddedInsurance: true } : {}),
        }
      }

      if (segment === FlightSegment.DEPARTING && currentSegment === FlightSegment.RETURNING) {
        return undefined
      }

      if (options.areAllFlightsCovered && flight) {
        flight.withEmbeddedInsurance = true
      }

      return flight
    })
    .filter((flight): flight is App.Checkout.FlightItemFlight => flight !== undefined)

  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    selectedCabinClass: options.selectedCabinClass,
    expiry,
    // our flight is changing, reset the fare fetching behaviour
    hasFinalFare: false,
    fareFetchError: undefined,
    journeyType: journey.itemType,
    forceBundleId: options.forceBundleId ?? checkoutItem.forceBundleId,
    totalFare: 0,
    searchId: checkoutItem.searchId,
    viewType: journey.fareType === FlightViewTypes.RETURN ? FlightViewTypes.TWO_ONE_WAYS_AND_RETURN : checkoutItem.viewType,
    paymentScheduleSupported: journey.paymentScheduleSupported ?? checkoutItem.paymentScheduleSupported,
    flights,
  }

  return updatedItem
}

export function resetFlightCheckoutItem(
  checkoutItem: App.Checkout.FlightItem,
) {
  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    flights: [],
  }

  return updatedItem
}

export function setFlightCheckoutItemJourneyFareFamily(
  checkoutItem: App.Checkout.FlightItem,
  flightIndex: number,
  options: {
    fareFamily?: App.FlightFareFamily
  },
) {
  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    flights: checkoutItem.flights.map((flight, index) => {
      if (index === flightIndex) {
        return {
          ...flight,
          fareFamily: options.fareFamily,
        }
      }

      return flight
    }),
  }

  return updatedItem
}

export function updateFlightCheckoutItem(
  checkoutItem: App.Checkout.FlightItem,
  updates: FlightItemUpdate,
): App.Checkout.FlightItem {
  const {
    originAirportCode,
    destinationAirportCode,
    searchId,
    forceBundleId,
  } = updates

  const flights = searchId !== checkoutItem.searchId ? [] : checkoutItem.flights

  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    originAirportCode: originAirportCode ?? checkoutItem.originAirportCode,
    destinationAirportCode: destinationAirportCode ?? checkoutItem.destinationAirportCode,
    searchId,
    forceBundleId,
    // we just updated the item, fare may have changed
    hasFinalFare: false,
    fareFetchError: undefined,
    flights,
  }

  return updatedItem
}

interface GenerateItemFields {
  originAirportCode: string
  destinationAirportCode?: string
  occupants: App.Occupants
  passengers: Array<App.Checkout.FlightPassenger>
  fareType: 'return' | 'oneWay' | 'multiCity'
  viewType: string
  searchId?: string
  isFlightCredit?: boolean
  itemId?: string
  fareFamilies?: Array<App.FlightFareFamily | undefined>
  baggage?: Array<App.Checkout.FlightItemPaxBaggageMap>
  flights?: Array<App.JourneyV2>
  bundledItemIds?: Array<string>
  quotedFare?: number
  expiry?: number
  selectedCabinClass?: FlightsClassTypes
  areAllFlightsCovered?: boolean
  reservationId?: string
  isFlightDateChange?: boolean
  charterBundleRoute?: App.CharterBundleRoute
}

export function generateFlightCheckoutItem(fields: GenerateItemFields): App.Checkout.FlightItem {
  const {
    searchId,
    flights = [],
    baggage = [],
    fareFamilies = [],
    occupants,
    passengers,
    fareType,
    viewType,
    isFlightCredit,
    itemId,
    bundledItemIds,
    originAirportCode,
    destinationAirportCode,
    quotedFare,
    expiry,
    selectedCabinClass,
    areAllFlightsCovered,
    reservationId,
    isFlightDateChange,
    charterBundleRoute,
  } = fields
  return {
    itemId: itemId ?? uuidV4(),
    transactionKey: uuidV4(),
    carrier: flights[0]?.flightGroup?.flights[0]?.marketingAirline.code,
    itemType: CHECKOUT_ITEM_TYPE_FLIGHT,
    originAirportCode,
    destinationAirportCode: destinationAirportCode!,
    passengers,
    occupants,
    fareType,
    viewType: getViewType(flights[0], flights[1], viewType),
    quotedFare,
    journeyType: flights[0]?.itemType,
    isFlightCredit: !!isFlightCredit,
    searchId,
    reservationId,
    bundledItemIds,
    expiry,
    selectedCabinClass,
    charterBundleRoute,
    flights: flights.map((flight, index) => {
      const journey = getJourneyFields(flight)
      return {
        carrier: flight.flightGroup.flights[0].marketingAirline.code,
        journeyId: journey.journeyId,
        journeyKey: journey.journeyKey,
        provider: journey.provider,
        providerSearchId: journey.providerSearchId,
        fareFamily: fareFamilies[index],
        extras: baggage[index] ?? EmptyBaggage,
        cost: journey.cost,
        carrierImage: journey.carrierImage,
        departingAirportCode: flight.flightGroup.flights[0].departureAirport,
        arrivalAirportCode: last(flight.flightGroup.flights).arrivalAirport,
        departingDate: flight.flightGroup.flights[0].departingDate,
        arrivalAirportName: last(flight.flightGroup.flights).arrivalAirportName,
        departingAirportName: flight.flightGroup.flights[0].departureAirportName,
        arrivalDateTime: last(flight.flightGroup.flights).arrivalDateTime,
        departureDateTime: flight.flightGroup.flights[0].departureDateTime,
        duration: flight.flightGroup.totalFlightDuration,
        numberOfStops: flight.flightGroup.flights.length - 1,
        hasIncludedCheckedBaggage: journey.hasIncludedCheckedBaggage,
        hasIncludedCarryOnBaggage: journey.hasIncludedCarryOnBaggage,
        paymentScheduleSupported: flight.paymentScheduleSupported,
        layoverDuration: flight.flightGroup.totalStopOverTime,
        ...(areAllFlightsCovered ? { withEmbeddedInsurance: true } : {}),
      }
    }),
    hasFinalFare: false,
    isFlightDateChange,
    paymentScheduleSupported: flights[0]?.paymentScheduleSupported ?? false,
  }
}

export function generateAccommodationBundleFlightItems({
  rooms,
  airport,
  offer,
  accommodationItems,
  quotedFare,
  charterBundleRoute,
  fareClass,
  departingCharterFlightJourney,
  returningCharterFlightJourney,
  departingCharterFareFamily,
  returningCharterFareFamily,
  charterFlightSearchResultsTimestamp,
  charterFlightSearchId,
}: {
  rooms: Array<App.RoomOccupants>
  airport: App.Airport
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem>
  quotedFare: number | undefined
  charterBundleRoute?: App.CharterBundleRoute
  fareClass?: FlightsClassTypes
  departingCharterFlightJourney?: App.JourneyV2
  returningCharterFlightJourney?: App.JourneyV2
  departingCharterFareFamily?: App.FlightFareFamily
  returningCharterFareFamily?: App.FlightFareFamily
  charterFlightSearchResultsTimestamp?: number
  charterFlightSearchId?: string
  charterFlightQuotedFare?: number
  charterFeederFlightQuotedFare?: number
}): Array<App.Checkout.FlightItem> {
  const flightItems: Array<App.Checkout.FlightItem> = []
  const flightOccupants = countOccupantsForFlights(rooms)
  const charterBundleRouteInfo = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined

  if (charterBundleRouteInfo && departingCharterFlightJourney && returningCharterFlightJourney && charterFlightSearchResultsTimestamp && charterFlightSearchId && departingCharterFareFamily && returningCharterFareFamily) {
    const seatEligiblePassengers = flightOccupants.adults + flightOccupants.children + flightOccupants.teenagers
    const charterFlightPrice =
      getEffectiveFlightPrice(departingCharterFlightJourney, seatEligiblePassengers).all.totalFare +
      getEffectiveFlightPrice(returningCharterFlightJourney, seatEligiblePassengers).all.totalFare

    const charterSearchExpiry = getFlightExpiry(departingCharterFlightJourney, 0)

    // Charter flight
    const baseCharterFlightItem = generateFlightCheckoutItem({
      originAirportCode: charterBundleRouteInfo.originAirport.airportCode,
      destinationAirportCode: charterBundleRouteInfo.destinationAirport.airportCode,
      bundledItemIds: accommodationItems.map(item => item.itemId),
      occupants: flightOccupants,
      fareType: 'return',
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      passengers: generatePassengersFromOccupants(flightOccupants),
      quotedFare: charterFlightPrice,
      searchId: charterFlightSearchId,
      charterBundleRoute,
    })

    const departingCharterFlightItem = setFlightCheckoutItemJourney(
      baseCharterFlightItem,
      departingCharterFlightJourney,
      'departing',
      charterSearchExpiry,
      {
        forceBundleId: offer.forceBundleId,
        selectedCabinClass: fareClass,
        fareFamily: departingCharterFareFamily,
      },
    )

    const charterFlightItem = setFlightCheckoutItemJourney(
      departingCharterFlightItem,
      returningCharterFlightJourney,
      'returning',
      charterSearchExpiry,
      {
        forceBundleId: offer.forceBundleId,
        selectedCabinClass: fareClass,
        fareFamily: returningCharterFareFamily,
      },
    )

    flightItems.push(charterFlightItem)

    if (airport.code !== charterBundleRouteInfo.originAirport.airportCode) {
      // Feeder flight
      flightItems.push(generateFlightCheckoutItem({
        originAirportCode: airport.code,
        destinationAirportCode: charterBundleRouteInfo.originAirport.airportCode,
        bundledItemIds: accommodationItems.map(item => item.itemId),
        occupants: flightOccupants,
        fareType: 'return',
        viewType: FlightViewTypes.TWO_ONE_WAYS,
        passengers: generatePassengersFromOccupants(flightOccupants),
        quotedFare: quotedFare ? quotedFare - charterFlightPrice : undefined,
      }))
    }
  } else {
    // Normal flight
    flightItems.push(generateFlightCheckoutItem({
      originAirportCode: airport.code,
      destinationAirportCode: offer.flightDestinationPort,
      bundledItemIds: accommodationItems.map(item => item.itemId),
      occupants: flightOccupants,
      fareType: 'return',
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      passengers: generatePassengersFromOccupants(flightOccupants),
      quotedFare,
    }))
  }

  return flightItems
}
